import { apiFetch, ApiError } from "./api";
import {
  readHubSupportConsoleCapturePayload,
  recordHubSupportConsoleLine,
} from "./installHubSupportConsoleCapture";
import { hubSupportTicketPagePath, hubSupportTicketPageUrlFull } from "./navigationTrail";
import {
  poolHolderBannerRows,
  poolHolderCapacityText,
  processWaiterBannerRows,
} from "./slowRequestBanner";
import {
  buildSlowHubQueueReport,
  buildSlowHubSupportTicketPrefill,
  isClientNetworkUnreachable,
  slowHubSupportDedupeKey,
  type SlowHubNetworkEvidence,
  type SlowHubSupportTicketContext,
} from "./slowRequestSupportTicketCore";
import {
  collectSlowRequestNetworkDiagnostics,
  type NetworkProbeResult,
} from "./slowRequestNetworkDiagnostics";

export {
  buildSlowHubSupportTicketPrefill,
  isClientNetworkUnreachable,
  isHubDevInstance,
  shouldAutoReportSlowHub,
  slowHubSupportDedupeKey,
  SLOW_HUB_AUTO_REPORT_ELAPSED_SECONDS,
  SLOW_HUB_AUTO_REPORT_MIN_REQUESTS,
  SLOW_HUB_SUPPORT_DEDUPE_WINDOW_MS,
} from "./slowRequestSupportTicketCore";
export type { SlowHubSupportSlowRow, SlowHubSupportTicketContext } from "./slowRequestSupportTicketCore";

const AUTO_REPORT_STORAGE_PREFIX = "hub-slow-auto-report:";
const PENDING_REPORT_STORAGE_KEY = "hub-slow-support-pending-v1";

export interface SlowHubSupportTicketSubmitResult {
  ok: boolean;
  ticketId: string | null;
  dedupeSkipped: boolean;
  queuedLocally: boolean;
  /** Auto-report suppressed because this browser could not reach the hub at all. */
  skippedClientNetwork: boolean;
  /** Server dropped an automatic report because the page came from a dev origin. */
  devOriginSkipped: boolean;
  errorMessage: string | null;
}

function toProbeEvidence(value: unknown): SlowHubNetworkEvidence["mainOriginProbe"] {
  if (value == null || typeof value !== "object") return null;
  const probe = value as NetworkProbeResult;
  return { outcome: probe.outcome, status: probe.status };
}

function toNetworkEvidence(network: Record<string, unknown>): SlowHubNetworkEvidence {
  return {
    mainOriginProbe: toProbeEvidence(network.mainOriginProbe),
    liveOriginProbe: toProbeEvidence(network.liveOriginProbe),
    online: typeof network.online === "boolean" ? network.online : null,
  };
}

export function hasSlowHubAutoReportMarker(dedupeKey: string): boolean {
  try {
    return sessionStorage.getItem(`${AUTO_REPORT_STORAGE_PREFIX}${dedupeKey}`) != null;
  } catch {
    return false;
  }
}

export function markSlowHubAutoReportMarker(dedupeKey: string, ticketId: string): void {
  try {
    sessionStorage.setItem(`${AUTO_REPORT_STORAGE_PREFIX}${dedupeKey}`, ticketId);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Probe origins once, attach the evidence to the context so the classifier can
 * see it, and write the diagnostic snapshot line into the console capture.
 * Returns the enriched context.
 */
export async function appendSlowHubDiagnosticsToSupportCapture(
  baseContext: SlowHubSupportTicketContext,
): Promise<SlowHubSupportTicketContext> {
  const network = await collectSlowRequestNetworkDiagnostics();
  const context: SlowHubSupportTicketContext = {
    ...baseContext,
    network: toNetworkEvidence(network),
  };
  const prefill = buildSlowHubSupportTicketPrefill(context);
  const queueReport = buildSlowHubQueueReport(context);
  const holderCapacity =
    context.holderSnapshot != null ? poolHolderCapacityText(context.holderSnapshot) : null;
  const holderRows =
    context.holderSnapshot != null
      ? poolHolderBannerRows(context.holderSnapshot, context.nowMs).slice(0, 20)
      : [];
  const processWaiterRows =
    context.holderSnapshot != null
      ? processWaiterBannerRows(context.holderSnapshot, context.nowMs).slice(0, 20)
      : [];
  recordHubSupportConsoleLine("warn", {
    event: "slow_hub_support_snapshot",
    trigger: context.trigger,
    pagePath: hubSupportTicketPagePath(window.location.pathname, window.location.search),
    holderStreamStatus: context.holderStreamStatus,
    holderCapacity,
    holderRows,
    processWaiterRows,
    network,
    queueReport,
    queues: context.holderSnapshot?.queues ?? [],
    prefill,
  });
  return context;
}

interface PendingSlowHubSupportPayload {
  dedupeKey: string | null;
  actualBehavior: string;
  expectedBehavior: string;
  consoleCapture: string;
  pagePath: string;
  pageUrlFull: string;
  userAgent: string;
}

function readPendingSlowHubSupportPayload(): PendingSlowHubSupportPayload | null {
  try {
    const raw = sessionStorage.getItem(PENDING_REPORT_STORAGE_KEY);
    if (raw == null || raw.trim() === "") {
      return null;
    }
    return JSON.parse(raw) as PendingSlowHubSupportPayload;
  } catch {
    return null;
  }
}

function writePendingSlowHubSupportPayload(payload: PendingSlowHubSupportPayload | null): void {
  try {
    if (payload == null) {
      sessionStorage.removeItem(PENDING_REPORT_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_REPORT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

async function postSlowHubSupportTicket(input: PendingSlowHubSupportPayload): Promise<{
  ticketId: string | null;
  dedupeSkipped: boolean;
  devOriginSkipped: boolean;
}> {
  const result = await apiFetch<{
    ok: boolean;
    id: string | null;
    dedupeSkipped?: boolean;
    devOriginSkipped?: boolean;
  }>(
    "/api/hub-support-tickets",
    {
      method: "POST",
      body: {
        kind: "bug",
        actualBehavior: input.actualBehavior,
        expectedBehavior: input.expectedBehavior,
        consoleCapture: input.consoleCapture,
        pagePath: input.pagePath,
        pageUrlFull: input.pageUrlFull,
        userAgent: input.userAgent,
        dedupeKey: input.dedupeKey,
      },
    },
  );
  return {
    ticketId: result.id ?? null,
    dedupeSkipped: result.dedupeSkipped === true,
    devOriginSkipped: result.devOriginSkipped === true,
  };
}

/** Server dropped an automatic report from a dev origin: nothing to retry, nothing to mark. */
function devOriginSkippedResult(): SlowHubSupportTicketSubmitResult {
  return {
    ok: false,
    ticketId: null,
    dedupeSkipped: false,
    queuedLocally: false,
    skippedClientNetwork: false,
    devOriginSkipped: true,
    errorMessage: null,
  };
}

export async function submitSlowHubSupportTicket(
  baseContext: SlowHubSupportTicketContext,
): Promise<SlowHubSupportTicketSubmitResult> {
  const context = await appendSlowHubDiagnosticsToSupportCapture(baseContext);
  if (context.trigger === "auto" && isClientNetworkUnreachable(context.network)) {
    // The browser cannot reach the hub at all. Filing a "hub slowdown" ticket
    // (or queueing one to send later) would blame the server for a client-side
    // network loss, so skip the auto-report. Manual reports still go through.
    return {
      ok: false,
      ticketId: null,
      dedupeSkipped: false,
      queuedLocally: false,
      skippedClientNetwork: true,
      devOriginSkipped: false,
      errorMessage: null,
    };
  }
  const prefill = buildSlowHubSupportTicketPrefill(context);
  const pagePath = hubSupportTicketPagePath(window.location.pathname, window.location.search);
  const dedupeKey = context.trigger === "auto" ? slowHubSupportDedupeKey(pagePath, context.nowMs) : null;
  const payload: PendingSlowHubSupportPayload = {
    dedupeKey,
    actualBehavior: prefill.actualBehavior,
    expectedBehavior: prefill.expectedBehavior,
    consoleCapture: readHubSupportConsoleCapturePayload() ?? "",
    pagePath,
    pageUrlFull: hubSupportTicketPageUrlFull(window.location),
    userAgent: navigator.userAgent,
  };

  try {
    const result = await postSlowHubSupportTicket(payload);
    writePendingSlowHubSupportPayload(null);
    if (result.ticketId == null) {
      return devOriginSkippedResult();
    }
    if (dedupeKey != null) {
      markSlowHubAutoReportMarker(dedupeKey, result.ticketId);
    }
    return {
      ok: true,
      ticketId: result.ticketId,
      dedupeSkipped: result.dedupeSkipped,
      queuedLocally: false,
      skippedClientNetwork: false,
      devOriginSkipped: false,
      errorMessage: null,
    };
  } catch (caught) {
    writePendingSlowHubSupportPayload(payload);
    const message = caught instanceof ApiError ? caught.message : "Could not send support ticket.";
    return {
      ok: false,
      ticketId: null,
      dedupeSkipped: false,
      queuedLocally: true,
      skippedClientNetwork: false,
      devOriginSkipped: false,
      errorMessage: message,
    };
  }
}

/** Retry a ticket saved locally when the hub was too slow to accept POST. */
export async function flushPendingSlowHubSupportTicket(): Promise<SlowHubSupportTicketSubmitResult | null> {
  const pending = readPendingSlowHubSupportPayload();
  if (pending == null) {
    return null;
  }
  try {
    const result = await postSlowHubSupportTicket(pending);
    writePendingSlowHubSupportPayload(null);
    if (result.ticketId == null) {
      return devOriginSkippedResult();
    }
    if (pending.dedupeKey != null) {
      markSlowHubAutoReportMarker(pending.dedupeKey, result.ticketId);
    }
    return {
      ok: true,
      ticketId: result.ticketId,
      dedupeSkipped: result.dedupeSkipped,
      queuedLocally: false,
      skippedClientNetwork: false,
      devOriginSkipped: false,
      errorMessage: null,
    };
  } catch (caught) {
    const message = caught instanceof ApiError ? caught.message : "Could not send support ticket.";
    return {
      ok: false,
      ticketId: null,
      dedupeSkipped: false,
      queuedLocally: true,
      skippedClientNetwork: false,
      devOriginSkipped: false,
      errorMessage: message,
    };
  }
}

export function resetSlowHubSupportTicketStateForTests(): void {
  writePendingSlowHubSupportPayload(null);
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key != null && key.startsWith(AUTO_REPORT_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
}
